import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";

const EditDepartment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "department"
  );

  const [department, setDepartment] = useState({ name: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const canViewPage = hasModulePermission("department.pages.edit.view");
  const canSubmit =
    hasModulePermission("department.actions.update") &&
    hasModulePermission("department.actions.submit_update");
  const canOpenList =
    hasModulePermission("department.pages.list.view") &&
    hasModulePermission("department.actions.open_list");

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage) {
      setPageLoading(false);
      return;
    }

    const fetchDepartment = async () => {
      try {
        setPageLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_URL}/departments/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to fetch department");

        setDepartment({ name: data.data?.name || "" });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || "Error loading department",
          color: "error",
        });

        if (error.message?.toLowerCase().includes("not found")) {
          navigate("/departments");
        }
      } finally {
        setPageLoading(false);
      }
    };

    fetchDepartment();
  }, [canViewPage, id, isAuthReady, navigate]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to update departments.",
        color: "error",
      });
      return;
    }

    if (!department.name.trim()) {
      setSnackbar({ open: true, message: "Department name is required.", color: "error" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/departments/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: department.name.trim() }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update department");

      setSnackbar({
        open: true,
        message: data.message || "Department updated successfully",
        color: "success",
      });
      setTimeout(() => navigate("/departments"), 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Update failed",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress size={40} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Department
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to edit departments.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        open={snackbar.open}
        color={snackbar.color}
        icon="notifications"
        title="Department"
        content={snackbar.message}
        close={closeSnackbar}
        onClose={closeSnackbar}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Department
          </SoftTypography>
          {canOpenList && (
            <SoftButton
              size="small"
              color="info"
              onClick={() => navigate("/departments")}
              startIcon={<Icon>list</Icon>}
            >
              Department List
            </SoftButton>
          )}
        </SoftBox>

        <form onSubmit={handleUpdate}>
          {canViewField("name") && (
            <SoftBox mb={3}>
              <SoftTypography variant="caption" fontWeight="bold" display="block" mb={1}>
                Department Name
              </SoftTypography>
              <SoftInput
                placeholder="Enter department name"
                value={department.name}
                onChange={(e) => setDepartment({ ...department, name: e.target.value })}
                fullWidth
              />
            </SoftBox>
          )}

          {canSubmit && (
            <SoftBox mt={4}>
              <SoftButton
                type="submit"
                color="info"
                variant="gradient"
                disabled={loading}
                fullWidth
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? "Processing..." : "Update Department"}
              </SoftButton>
            </SoftBox>
          )}
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default EditDepartment;
